import React, { useRef } from "react";
import { styled } from "@mui/material/styles";
import { withStyles } from "@mui/styles";
import { AccordionSummary, Accordion, AccordionDetails } from "@mui/material";
import { featureToLines } from "fe-common/logic";
import { SummaryDiseaseClusterFragment } from "fe-common/graphql";
import { CommonColors } from "fe-web-components/colors/commonColors";
import { LLMDisclaimer } from "fe-web-components/layout/LLMDisclaimer";
import { ClinicColors } from "../styles/clinicTheme";
//import { GroundedDataParagraph } from "../grounding/grounding";
import { CSSCollapseTransitionComponent } from "./layout/CSSCollapseTransitionComponent";
import { StrengthIndicator } from "fe-web-components/patientCard/strengthIndicator";
import { OrganSystemIcon } from "fe-web-components/patientCard/commonOrganSystemIcon";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { GroundingRag } from "../grounding/groundingRAG";

//region [[ Styles ]]
const FeatureItemView = styled("div")({
  position: "relative",
  borderRadius: 5,
  boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.1);",
  backgroundColor: "#FFFFFF",
  overflow: "hidden",
  "&.simple-view": {
    boxShadow: "none",
  },
});
const DDAccordion = withStyles({
  root: {
    backgroundColor: "transparent",
    boxShadow: "none",
    borderRadius: "5px !important",
    padding: 16,
    border: "none",
    margin: "0 !important",
    "&$expanded": { marginBottom: 0 },
    ".simple-view &": {
      padding: "3px 0",
    },
  },
})(Accordion);

const DDAccordionSummary = withStyles({
  root: {
    backgroundColor: "transparent",
    border: "none",
    padding: 0,
    minHeight: 0,
    "&$expanded": { minHeight: 0, margin: 0 },
    "& .grounded svg": {
      fill: ClinicColors.primary,
    },
    "& .generating svg": {
      fill: ClinicColors.borders,
    },
    "& .strength": {
      fontSize: 12,
      color: ClinicColors.primary,
    },
    ".simple-view &": {
      display: "inline-flex",
      gap: 5,
    },
  },
  content: {
    alignItems: "center",
    gap: 6,
    margin: 0,
    "&$expanded": { margin: 0, fontWeight: "600" },
  },
  expanded: {},
})(AccordionSummary);

const DDAccordionDetails = withStyles({
  root: {
    backgroundColor: CommonColors.ClinicTextBackground,
    padding: 12,
    fontSize: 13,
    margin: "16px 0 6px",
    borderRadius: 4,
    display: "block",
    ".simple-view &": {
      padding: 0,
      backgroundColor: CommonColors.White,
    },
  },
})(AccordionDetails);

const PatientCardStrengthIndicator = styled(StrengthIndicator)({
  background: CommonColors.Primary,
  left: 0,
  maxWidth: "100%",
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
});
//endregion [[ Styles ]]

//region [[ Props ]]
interface FeatureItemProps {
  cluster: SummaryDiseaseClusterFragment;
  onClick?: () => void;
  strength?: number;
  inExtension?: boolean;
  index?: number;
  simpleView?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

// const GroundingIcon = (props: {
//   generating: boolean;
//   percent: number;
//   inExtension?: boolean;
// }) => {
//   const { generating, percent } = props;
//   return (
//     generating && (
//       <Tooltip
//         TransitionComponent={
//           props.inExtension ? CSSCollapseTransitionComponent : undefined
//         }
//         title={
//           generating
//             ? "Not grounded yet..."
//             : percent
//             ? `${percent}% grounded`
//             : "Grounded"
//         }
//         arrow
//         placement="top"
//       >
//         <span className={generating ? "generating" : "grounded"}>
//           <ValidatedIcon />
//         </span>
//       </Tooltip>
//     )
//   );
// };

const CommonPatientCardFeatureItem = ({
  cluster,
  onClick,
  strength,
  simpleView,
  ...props
}: FeatureItemProps) => {
  const grounding = cluster?.explanation?.grounding;
  const groundedData = grounding?.grounding;
  const grounded = groundedData?.sentences?.length;
  const containerRef = useRef();

  return (
    <FeatureItemView
      className={`${props.index === 0 ? "first-dd-item" : ""} ${
        simpleView ? "simple-view" : ""
      }`}
    >
      <DDAccordion
        TransitionComponent={
          props.inExtension ? CSSCollapseTransitionComponent : undefined
        }
        TransitionProps={
          {
            // nodeRef: props.inExtension ? containerRef.current : undefined,
          }
        }
      >
        <DDAccordionSummary
          onClick={onClick}
          ref={containerRef}
          expandIcon={
            cluster?.explanation?.text ? <ExpandMoreIcon /> : <span />
          }
        >
          {!simpleView && (
            <OrganSystemIcon feature={cluster.title} showBackground />
          )}
          {/*if the title is missing use the cluster name (e.g. dummy features from llm)*/}
          {simpleView && `${props.index + 1}. `}
          {featureToLines(cluster.title)?.toString() ||
            cluster.title?.name ||
            ""}
        </DDAccordionSummary>
        {!!cluster?.explanation?.text && (
          <DDAccordionDetails>
            <LLMDisclaimer
              iconStyle={{ float: "inline-end" }}
              inExtension={props.inExtension}
            />
            {grounded ? (
              <GroundingRag
                groundedData={groundedData}
                // outputReferenceSection={false}
                // inExtension={props.inExtension}
              />
            ) : (
              cluster?.explanation?.text
            )}
          </DDAccordionDetails>
        )}
      </DDAccordion>
      {!simpleView && strength && (
        <PatientCardStrengthIndicator strength={strength} />
      )}
    </FeatureItemView>
  );
};

export default CommonPatientCardFeatureItem;
